import React, { useCallback, useMemo } from "react";

import CircleIcon from "@mui/icons-material/Circle";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Chip, Fade, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { isBefore, startOfDay } from "date-fns";

import useGetFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import { tasksKeyMakerV2 } from "@fartherfinance/frontend/api/Tasks/hooks/useGetTasks.v2";
import { TaskStatus, TaskV3 } from "@fartherfinance/frontend/api/Tasks/Types";
import { isGroupAssignee } from "@fartherfinance/frontend/api/Tasks/utils/isGroupAssignee";
import { mapTaskTypeToLabelV2 } from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import { PaginationHeaders } from "@fartherfinance/frontend/api/utils/parsePaginationHeaders";

import { UNKNOWN_GROUP, UNKNOWN_USER, UNKNOWN_VALUE } from "../../common";
import { assigneeDefaultOption, TASKS_DEFAULT_PAGE } from "../common";
import { useTasksFiltersV3 } from "../contextV3";
import { useTasksNavigation } from "../hooks";
import TaskActor from "../TaskActor/TaskActor";
import TaskStatusChip, {
  mapTaskStatusToLabelV2,
} from "../TaskStatusChip/TaskStatusChip";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { mapToSortOrder } from "@src/multiCustodian/utils/sorting";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { Table } from "@src/sharedComponents/TableV2/Table";
import { TableSortOrder } from "@src/sharedComponents/TableV2/TableHeader";
import { TooltipWrapper } from "@src/sharedComponents/TooltipWrapper/TooltipWrapper";
import Text from "@src/sharedComponents/Typography/Text";

import TableTaskTitle from "./Components/TableTaskTitle/TableTaskTitle";
import { createTableColumns, formatTaskDate } from "./TasksTable.utils";

import styles from "./TasksTable.module.css";

export type TaskTableColumns =
  | keyof Pick<
      TaskV3,
      | "title"
      | "client"
      | "advisor"
      | "assignee"
      | "taskStatus"
      | "taskType"
      | "updatedTs"
      | "dueDate"
    >
  | "action"
  | "actionLeft";

interface TasksTableProps {
  tasks: TaskV3[];
  isLoading: boolean;
  isErrored: boolean;
  pagination: PaginationHeaders | undefined;
}

const COLUMN_WIDTHS_LOCAL_STORAGE_KEY = "farther:layout:task-column-widths";

export default function TasksTable({
  tasks,
  isLoading,
  isErrored,
  pagination,
}: TasksTableProps): JSX.Element {
  const queryClient = useQueryClient();

  const { handleNavigateToTaskDetails } = useTasksNavigation();
  const {
    queryParams: { sort },
    handleChangeQueryParams,
    handleResetQueryParams,
  } = useTasksFiltersV3();

  const auth = useAdvisorRequestAuth();
  const advisorsQuery = useGetFartherEmployees("All", auth);

  const advisors = useMemo(
    () => advisorsQuery.data?.advisors ?? [],
    [advisorsQuery]
  );

  const handleOnClearButtonClick = (): void => {
    handleResetQueryParams();
  };

  const handleOnPageChange = (page: number): void => {
    handleChangeQueryParams({ page });
  };

  const handleTasksListRefetch = (): void => {
    queryClient.invalidateQueries(tasksKeyMakerV2(null));
  };

  const handleOnHeaderClick = (
    column: TaskTableColumns,
    tableSortOrder: TableSortOrder
  ): void => {
    if (
      column === "client" ||
      column === "assignee" ||
      column === "advisor" ||
      column === "dueDate" ||
      column === "updatedTs" ||
      column === "taskStatus" ||
      column === "taskType"
    ) {
      handleChangeQueryParams({
        sort: `${mapToSortOrder(tableSortOrder)}${column}`,
        page: TASKS_DEFAULT_PAGE,
      });
    }
  };

  const isTaskDue = useCallback((task: TaskV3): boolean => {
    if (!task.dueDate) {
      return false;
    }

    if (
      task.taskStatus === TaskStatus.enum.COMPLETED ||
      task.taskStatus === TaskStatus.enum.CANCELED
    ) {
      return false;
    }

    return isBefore(task.dueDate, startOfDay(new Date()));
  }, []);

  const isSortedByUpdatedTs = sort?.includes("updatedTs");
  const isSortedByDueDate = sort?.includes("dueDate");
  const tableHeaders = useMemo(() => createTableColumns(sort), [sort]);
  const tableColumnWidths = useMemo(() => {
    const persistedColumnWidths = localStorage.getItem(
      COLUMN_WIDTHS_LOCAL_STORAGE_KEY
    );

    return persistedColumnWidths
      ? JSON.parse(persistedColumnWidths)
      : [
          "max-content",
          "120px",
          "120px",
          "120px",
          "120px",
          "160px",
          "min-content",
          "min-content",
        ];
  }, []);

  return (
    <>
      <Table
        className={styles.table}
        initialColumnWidths={tableColumnWidths}
        externalStorage={localStorage}
        externalStorageKey={COLUMN_WIDTHS_LOCAL_STORAGE_KEY}
      >
        <Table.Row className={styles.header}>
          {tableHeaders.map((header, index) => (
            <Table.Header
              key={header.accessor}
              columnIndex={index}
              sortable={header.sortable}
              active={header.active}
              sortOrder={header.sortOrder}
              onClick={(tableSortOrder) =>
                handleOnHeaderClick(header.accessor, tableSortOrder)
              }
            >
              {header.label}
            </Table.Header>
          ))}
        </Table.Row>

        {isErrored && (
          <Table.Row className={styles.noTasks}>
            <Table.Cell>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                style={{
                  justifyContent: "center",
                  marginBottom: "15px",
                }}
              >
                <ErrorIcon className={styles.icon} />
                <Text>Error loading tasks</Text>
              </Stack>
              <Button
                text="Refresh"
                size="small"
                variant="outlined"
                buttonType="primary"
                onClick={handleTasksListRefetch}
                endIcon={<RefreshIcon className={styles.icon} />}
              />
            </Table.Cell>
          </Table.Row>
        )}

        {!isErrored && tasks.length === 0 && !isLoading && (
          <Table.Row className={styles.noTasks}>
            <Table.Cell>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                style={{
                  justifyContent: "center",
                  marginBottom: "15px",
                }}
              >
                <ErrorIcon className={styles.icon} />
                <Text>No tasks matching the filters</Text>
              </Stack>
              <Button
                text="Clear"
                size="small"
                variant="outlined"
                buttonType="primary"
                onClick={handleOnClearButtonClick}
                endIcon={<ClearIcon className={styles.icon} />}
              />
            </Table.Cell>
          </Table.Row>
        )}

        {isLoading && (
          <Fade in>
            <div className={styles.loadingContainer}>
              {Array.from({ length: 20 }).map((_, index) => (
                <Table.Row key={index}>
                  {Array.from({ length: tableHeaders.length }).map((_, idx) => (
                    <Table.Cell key={index + idx}>
                      <Skeleton width="100%" />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </div>
          </Fade>
        )}

        {!isLoading &&
          tasks.map((task) => {
            const advisorAvatarSrc = advisors.find(
              (advisor) => advisor.advisorId === task.advisor?.id
            )?.details.headshotPath;

            const assigneeAvatarSrc = advisors.find(
              (advisor) => advisor.advisorId === task.assignee?.id
            )?.details.headshotPath;

            return (
              <Table.Row
                key={task.id}
                onClick={() => handleNavigateToTaskDetails(task.id)}
              >
                <Table.Cell>
                  <TooltipWrapper tooltipText={task.title} index={0}>
                    <TableTaskTitle task={task} />
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper
                    tooltipText={
                      task.assignee
                        ? isGroupAssignee(task.assignee)
                          ? task.assignee.groupName ?? UNKNOWN_GROUP
                          : task.assignee.personName
                          ? `${task.assignee.personName.first} ${task.assignee.personName.last}`
                          : UNKNOWN_USER
                        : assigneeDefaultOption.label
                    }
                    index={1}
                  >
                    {task.assignee ? (
                      isGroupAssignee(task.assignee) ? (
                        <TaskActor
                          groupName={task.assignee.groupName ?? UNKNOWN_GROUP}
                        />
                      ) : task.assignee.personName ? (
                        <TaskActor
                          avatarSrc={assigneeAvatarSrc}
                          actorName={task.assignee.personName}
                        />
                      ) : (
                        <TaskActor groupName={UNKNOWN_USER} />
                      )
                    ) : (
                      <TaskActor groupName={assigneeDefaultOption.label} />
                    )}
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper
                    tooltipText={
                      task.client?.name
                        ? `${task.client.name.first} ${task.client.name.last}`
                        : UNKNOWN_USER ?? UNKNOWN_VALUE
                    }
                    index={2}
                  >
                    {task.client ? (
                      task.client.name ? (
                        <TaskActor actorName={task.client.name} />
                      ) : (
                        <TaskActor groupName={UNKNOWN_USER} />
                      )
                    ) : (
                      UNKNOWN_VALUE
                    )}
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper
                    tooltipText={
                      task.advisor?.name
                        ? `${task.advisor.name.first} ${task.advisor.name.last}`
                        : UNKNOWN_USER ?? UNKNOWN_VALUE
                    }
                    index={3}
                  >
                    {task.advisor ? (
                      task.advisor.name ? (
                        <TaskActor
                          avatarSrc={advisorAvatarSrc}
                          actorName={task.advisor.name}
                        />
                      ) : (
                        <TaskActor groupName={UNKNOWN_USER} />
                      )
                    ) : (
                      UNKNOWN_VALUE
                    )}
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper
                    tooltipText={mapTaskStatusToLabelV2(task.taskStatus)}
                    index={4}
                  >
                    <TaskStatusChip taskStatus={task.taskStatus} />
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper
                    tooltipText={mapTaskTypeToLabelV2(task.taskType)}
                    index={5}
                  >
                    <Chip
                      classes={{
                        root: styles.chip,
                        label: styles.chipLabel,
                      }}
                      variant="outlined"
                      label={mapTaskTypeToLabelV2(task.taskType)}
                    />
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <Text
                    variant="body"
                    color={isSortedByUpdatedTs ? "text" : "textSubtle"}
                  >
                    {formatTaskDate(new Date(task.updatedTs))}
                  </Text>
                </Table.Cell>

                <Table.Cell>
                  {task.dueDate ? (
                    <div className={styles.overdue}>
                      <Text
                        variant="body"
                        color={isSortedByDueDate ? "text" : "textSubtle"}
                      >
                        {formatTaskDate(task.dueDate)}
                      </Text>
                      {isTaskDue(task) && (
                        <CircleIcon className={styles.overdueIcon} />
                      )}
                    </div>
                  ) : (
                    UNKNOWN_VALUE
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
      </Table>

      {pagination && (
        <Table.Pagination
          pageSize={pagination.pageSize}
          currentPage={pagination.currentPage - 1}
          totalCount={pagination.totalCount}
          onPageChange={(page) => handleOnPageChange(page + 1)}
          isPrevButtonDisabled={pagination.prevPage === undefined}
          isNextButtonDisabled={pagination.nextPage === undefined}
        />
      )}
    </>
  );
}
